import { useMemo, useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';

import {
  useOrder,
  SET_ORDER_DETAILS,
  SET_PRODUCTS,
} from '../../hooks/useOrder';
import OrderPage from '../../components/payments/OrderPage';
import { useAuth } from '../../hooks/useAuth';
import { getProduct } from '../../helpers/products';
import { getInsightDiscount } from '../../helpers/pricing';
import { capitalize, currency } from '../../utils';
import MyOrderCard from '../../components/payments/MyOrderCard';

export default function OrderInsight() {
  const { state, dispatch, openOrder, loadOrder } = useOrder();
  const { orderStatus, products } = state;

  const { user } = useAuth();

  const location = useLocation();

  const orderDetails = useMemo(() => {
    let orderDetails = location.state?.orderDetails;
    if (!orderDetails) {
      const params = new URLSearchParams(window.location.search);
      return {
        orderId: params.get('orderId'),
        coreProductName: params.get('coreProductName'),
        fileId: params.get('fileId'),
        fileName: params.get('fileName'),
        matchedRecords: params.get('matchedRecords'),
      };
    }
    return orderDetails;
  }, [location.state]);

  const { orderId, coreProductName, fileId, fileName, matchedRecords } =
    orderDetails;

  useEffect(() => {
    dispatch({
      type: SET_ORDER_DETAILS,
      payload: orderDetails,
    });
  }, [orderDetails, dispatch]);

  useEffect(() => {
    if (orderDetails.coreProductName && orderStatus == null && !orderId) {
      const product = getProduct(orderDetails.coreProductName, user?.plan);
      dispatch({
        type: SET_PRODUCTS,
        payload: [{ ...product, quantity: orderDetails.recordsIncluded }],
      });
    }
  }, [dispatch, orderDetails, orderId, orderStatus, user?.plan]);

  useEffect(() => {
    if (orderId && orderStatus == null) {
      loadOrder({
        variables: {
          orderId,
          expandInvoice: true,
        },
      });
    }
  }, [loadOrder, orderId, orderStatus]);

  useEffect(() => {
    if (products?.length > 0 && orderStatus == null && !orderId) {
      openOrder({
        variables: {
          products,
          orderType: 'insight',
          orderDetails: {
            seedFile: fileId,
            seedFileName: fileName,
            packageName: 'Profile Report',
            recordsIncluded: matchedRecords,
          },
        },
      });
    }
  }, [
    fileId,
    fileName,
    matchedRecords,
    openOrder,
    orderId,
    orderStatus,
    products,
  ]);

  if (!coreProductName || !fileId || !fileName || !matchedRecords) {
    return <Navigate to="../../../mywork/files" />;
  }

  return (
    <OrderPage
      productLabel={null}
      productPricing={<ProductPricing />}
      productDetails={
        <ProductDetails
          fileName={fileName}
          matchedRecords={matchedRecords}
        />
      }
      orderSummary={
        <p className="text-lg">
          Ready to unlock new insights? You’re just a click away. If everything
          below looks correct, please click the <strong>Complete Order</strong> button below.
        </p>
      }
      backButton={null}
      showTotal={false}
      detailsPosition="left"
    />
  );
}

function ProductLabel({ fileName }) {
  return (
    <p>
      Seed File Name: <span className="font-bold">{fileName}</span>
    </p>
  );
}

function ProductDetails({ fileName, matchedRecords }) {
  const { state } = useOrder();
  const { totalPrice } = state;

  return (
    <div className="flex flex-col p-2 w-full" style={{ minWidth: 300 }}>
      <div className="mb-2">
        <h4 className="mb-0 text-xl font-bold">Profile Report Summary</h4>
      </div>
      <div className="flex flex-col bg-gray-100 rounded-half p-6 space-y-2">
        <p className="text-lg">
          Seed File Name: <span className="font-bold">{fileName}</span>
        </p>
        <p className="text-lg">
          # of Records:{' '}
          <span className="font-bold">
            {Number(matchedRecords).toLocaleString()}
          </span>
        </p>
        <p className="text-lg border-t pt-2 mt-2">
          Total Price: <span className="font-bold">{currency(totalPrice)}</span>
        </p>
      </div>
    </div>
  );
}

function ProductPricing() {
  const { user } = useAuth();
  const plan = user?.plan;

  if (!plan || plan === 'free') {
    return null;
  }

  const discount = getInsightDiscount(plan);

  if (plan === 'gold') {
    return (
      <p className="italic text-sm mt-4 -mb-3 w-full text-left">
        Profile Reports included with the {capitalize(plan)} plan
      </p>
    );
  }

  return (
    <p className="italic text-sm mt-4 -mb-3 w-full text-left">
      {discount * 100}% off discount on the {capitalize(plan)} plan has been
      applied
    </p>
  );
}
